import { Injectable } from '@angular/core';
import { AbstractControl, AsyncValidatorFn, FormGroup, ValidationErrors } from '@angular/forms';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { Id } from 'src/app/models/database/types/id.model';
import { AuthService } from '../http/auth/auth.service';
import { InsurancePlanService } from '../http/insurance-plan/insurance-plan.service';
import { InsuranceService } from '../http/insurance/insurance.service';
import { PepService } from '../http/pep/pep.service';
import { ProcedureService } from '../http/procedure/procedure.service';
import { ProceduresgroupService } from '../http/proceduresgroup.service';
import { ScheduleService } from '../http/schedule.service';
import { SchedulingService } from '../http/scheduling/scheduling.service';



@Injectable({
  providedIn: 'root'
})
export class AsyncValidatorsService {

  constructor(
    private _authService: AuthService,
    private _insuranceService: InsuranceService,
    private _insurancePlanService: InsurancePlanService,
    private _schedulingService: SchedulingService,
    private _procedureService: ProcedureService,
    private _scheduleService: ScheduleService,
    private _datasheetService: PepService,
    private _proceduresGroupService: ProceduresgroupService
  ) { }

  employeeCpfExists(initialValue?: string): AsyncValidatorFn {
    return (abstractControl: AbstractControl): Observable<ValidationErrors | null> => {
      if (abstractControl.value != initialValue || !initialValue) {
        return this._authService.verifyIfEmployeeExists({ cpf: abstractControl.value }).pipe(
          map(() => { return { cpfExists: true } }),
          catchError(error => of(null)))
      }
      return of(null)
    }
  }

  employeeEmailExists(initialValue?: string): AsyncValidatorFn {
    return (abstractControl: AbstractControl): Observable<ValidationErrors | null> => {
      if (abstractControl.value != initialValue || !initialValue) {
        return this._authService.verifyIfEmployeeExists({ email: abstractControl.value }).pipe(
          map(() => { return { emailExists: true } }),
          catchError(error => of(null)))
      }
      return of(null)
    }
  }

  employeeEmailDoesntExist(): AsyncValidatorFn {
    return (abstractControl: AbstractControl): Observable<ValidationErrors | null> => {
      return this._authService.verifyIfEmployeeExists({ email: abstractControl.value }).pipe(
        map(() => { return null }),
        catchError(error => of({ emailDoesntExist: true })))
    }
  }

  employeeRgExists(rg: string, rguf: Id): AsyncValidatorFn {
    return (formGroup: FormGroup): Observable<ValidationErrors | null> => {
      let rgValue = formGroup.value.rg
      let rgUfValue = formGroup.value.rgUf
      if (rgValue && rgUfValue
        && rgValue != rg && rgUfValue != rguf
        && rgValue !== "" && rgUfValue !== "") {
        return this._authService.verifyIfEmployeeExists({ rg: rgValue, rguf: rgUfValue })
          .pipe(
            map(() => { return { rgExists: true } }),
            catchError(error => of(null))
          )
      }
      return of(null)
    }
  }

  employeeCrmExists(crm: string, crmuf: Id): AsyncValidatorFn {
    return (formGroup: FormGroup): Observable<ValidationErrors | null> => {
      let crmValue = formGroup.value.crm
      let crmUfValue = formGroup.value.crmuf
      if (crmValue && crmUfValue
        && crmValue != crm && crmUfValue != crmuf
        && crmValue !== "" && crmUfValue !== "") {
        return this._authService.verifyIfEmployeeExists({ crm: crmValue, crmuf: crmUfValue })
          .pipe(
            map(() => { return { crmExists: true } }),
            catchError(error => of(null))
          )
      }
      return of(null)
    }
  }

  clinicCnpjExists(): AsyncValidatorFn {
    return (abstractControl: AbstractControl): Observable<ValidationErrors | null> => {
      return this._authService.verifyIfCnpjExists(abstractControl.value).pipe(
        map(() => { return { cnpjExists: true } }),
        catchError(error => of(null)))
    }
  }

  insuranceCodeExists(): AsyncValidatorFn {
    return (abstractControl: AbstractControl): Observable<ValidationErrors | null> => {
      return this._insuranceService.verifyIfCodeExists(abstractControl.value).pipe(
        map(() => { return { codeExists: true } }),
        catchError(error => of(null)))
    }
  }

  insurancePlanNameExists(insuranceId: Id): AsyncValidatorFn {
    return (abstractControl: AbstractControl): Observable<ValidationErrors | null> => {
      return this._insurancePlanService.getAllFromInsurance(insuranceId, { desc: abstractControl.value })
        .pipe(
          map(insurancePlans => { return { planNameExists: true } }),
          catchError(error => of(null)))
    }
  }

  schedulingDateIsValid(scheduleId: number): AsyncValidatorFn {
    return (abstractControl: AbstractControl): Observable<ValidationErrors | null> => {
      return this._schedulingService.validateDate(scheduleId, abstractControl.value)
        .pipe(
          map(() => null),
          catchError(error => of({ invalidDate: true }))
        )
    }
  }

  schedulingTimeIsValid(scheduleId: number, schedulingId: number = null): AsyncValidatorFn {
    return (abstractControl: AbstractControl): Observable<ValidationErrors | null> => {
      if (!scheduleId || !abstractControl.value['fitIntoSchedule'] || !abstractControl.value['date'] || !abstractControl.value['beginTime']) {
        return of(null)
      }
      return this._schedulingService.validateTime(scheduleId, abstractControl.value['date'], abstractControl.value['beginTime'], schedulingId)
        .pipe(
          map(() => null),
          catchError(error => of({ invalidTime: true }))
        )
    }
  }

  procedureDoesntExist(): AsyncValidatorFn {
    return (abstractControl: AbstractControl): Observable<ValidationErrors | null> => {
      if (!abstractControl.value) {
        return of(null)
      }
      return this._procedureService.exists({
        description: abstractControl.value,
        modalityId: abstractControl.value
      })
        .pipe(
          map(() => {
            return { procedureDoesntExist: true }
          }),
          catchError(error => {
            return of(null)
          })
        )
    }
  }

  proceduresGroupDoesntExist(): AsyncValidatorFn {
    return (abstractControl: AbstractControl): Observable<ValidationErrors | null> => {
      if (!abstractControl.value) {
        return of(null)
      }
      return this._proceduresGroupService.exists(abstractControl.value)
        .pipe(
          map(() => {
            return { proceduresGroupDoesntExist: true }
          }),
          catchError(error => {
            return of(null)
          })
        )
    }
  }

  scheduleDoesntExist(): AsyncValidatorFn {
    return (abstractControl: AbstractControl): Observable<ValidationErrors | null> => {
      if (!abstractControl.value) {
        return of(null)
      }
      return this._scheduleService.exists(abstractControl.value)
        .pipe(
          map(() => {
            return { scheduleDoesntExist: true }
          }),
          catchError(error => {
            return of(null)
          })
        )
    }
  }

  datasheetNameDoesntExist(datasheetId: number = null): AsyncValidatorFn {
    return (abstractControl: AbstractControl): Observable<ValidationErrors | null> => {
      if (!abstractControl.value) {
        return of(null)
      }
      return this._datasheetService.nameExists(abstractControl.value, datasheetId)
        .pipe(
          map(() => {
            return { datasheetNameDoesntExist: true }
          }),
          catchError(error => {
            return of(null)
          })
        )
    }
  }

  datasheetSectionDoesntExist(datasheetId: number, datasheetSectionId: number = null): AsyncValidatorFn {
    return (abstractControl: AbstractControl): Observable<ValidationErrors | null> => {
      if (!abstractControl.value) {
        return of(null)
      }
      return this._datasheetService.sectionExists(datasheetId, abstractControl.value, datasheetSectionId)
        .pipe(
          map(() => {
            return { datasheetSectionDoesntExist: true }
          }),
          catchError(error => {
            return of(null)
          })
        )
    }
  }

  datafieldDoesntExistInSection(sectionId: number, datafieldId: string = null): AsyncValidatorFn {
    return (abstractControl: AbstractControl): Observable<ValidationErrors | null> => {
      if (!abstractControl.value) {
        return of(null)
      }
      return this._datasheetService.datafieldExistsInSection(sectionId, abstractControl.value, datafieldId)
        .pipe(
          map(() => {
            return { datafieldDoesntExistInSection: true }
          }),
          catchError(error => {
            return of(null)
          })
        )
    }
  }
}
